import dayjs from "dayjs";
import {
  ENTRY_FEE,
  ENTRY_FEE_PGA,
  INITIAL_PURSE,
  INITIAL_PURSE_SEASON_3_FINAL,
  INITIAL_PURSE_ELEVATED,
  INITIAL_PURSE_PGA,
  TOURNAMENT_ID_SEASON_1_FINAL,
  PGA_TOURNAMENT_ID_SEASON_3,
  INITIAL_PURSE_NEXT_WORLD_SERIES,
  INITIAL_PURSE_NEXT_CHAMPIONSHIP,
  INITIAL_PURSE_PGA_AUSTRALIA,
  ENTRY_FEE_PGA_AUSTRALIA_SEASON_4,
  ENTRY_FEE_NEXT_SERIES,
  ENTRY_FEE_WITH_TAX,
  PRIZES_FINAL_SEASON_3,
  PRIZES_FINAL_SEASON_2,
  ENTRY_FEE_PGA_CANADA_SEASON_4,
  ENTRY_FEE_EPSON,
  INITIAL_PURSE_EPSON,
  INITIAL_PURSE_S5,
  ENTRY_FEE_S5,
  ENTRY_FEE_WITH_TAX_S5,
  INITIAL_PURSE_NEXT_WORLD_SERIES_S5,
  INITIAL_PURSE_NEXT_CHAMPIONSHIP_S5,
  ENTRY_FEE_NEXT_SERIES_S5,
  INITIAL_PURSE_PGA_S5,
  ENTRY_FEE_PGA_S5,
  INITIAL_PURSE_PGA_AUSTRALIA_S5,
  SPONSOR_PURSE_PGA_AUSTRALIA_S5,
  ENTRY_FEE_PGA_AUSTRALIA_S5,
  INITIAL_PURSE_PGA_CANADA_S5,
  SPONSOR_PURSE_PGA_CANADA_S5,
  ENTRY_FEE_PGA_CANADA_S5,
  INITIAL_PURSE_EPSON_S5,
  SPONSOR_PURSE_EPSON_S5,
  ENTRY_FEE_EPSON_S5,
  INITIAL_PURSE_CHALLENGERS_S5,
  ENTRY_FEE_CHALLENGERS_S5,
} from "./constants";
import { Tournament } from "../types/tournament";
import { prizeCalculatorPGA } from "./prizeCalculators/pga";
import { prizeCalculatorPGA_AU_CA } from "./prizeCalculators/season4/pga-australia-canada";
import { prizeCalculatorSeason3 } from "./prizeCalculators/season3";
import { prizeCalculatorSeason2 } from "./prizeCalculators/season2";
import { prizeCalculatorChampionship } from "./prizeCalculators/season4/championship";
import { prizeCalculatorEpson } from "./prizeCalculators/season4/epson";
import { prizeCalculatorWorldSeries } from "./prizeCalculators/season4/worldSeries";
import { prizeCalculatorPGA_AU_S5 } from "./prizeCalculators/season5/pga-australia";
import { prizeCalculatorPGA_CA_S5 } from "./prizeCalculators/season5/pga-canada";
import { prizeCalculatorChampionshipS5 } from "./prizeCalculators/season5/championship";
import { prizeCalculatorEpsonS5 } from "./prizeCalculators/season5/epson";
import { prizeCalculatorOpenQualifierS5 } from "./prizeCalculators/season5/open-qualifier";
import { prizeCalculatorWorldSeriesS5 } from "./prizeCalculators/season5/worldSeries";
import { prizeCalculatorChallengersS5 } from "./prizeCalculators/season5/challengers";
import { prizeCalculatorPGAAmericaS5 } from "./prizeCalculators/season5/pga-america";

export const isPossibleToJoin = (participants: number) => {
  return participants <= 1000;
};

// Both bounds are exclusive; tournaments outside these windows get season 0.
export const SEASON_DEFINITIONS = [
  { id: 1, start: "2023-01-01", end: "2023-04-03" },
  { id: 2, start: "2023-11-01", end: "2024-04-03" },
  { id: 3, start: "2024-10-01", end: "2025-04-03" },
  { id: 4, start: "2025-09-01", end: "2026-04-03" },
  { id: 5, start: "2026-08-01", end: "2027-05-01" },
];

export const SEASON_IDS = SEASON_DEFINITIONS.map(({ id }) => id);

/** Newest season that has already started, so the off-season keeps the last one. */
export const getCurrentSeason = () => {
  const started = SEASON_DEFINITIONS.filter(({ start }) =>
    dayjs().isAfter(dayjs(start)),
  );

  return started.length
    ? Math.max(...started.map(({ id }) => id))
    : SEASON_IDS[0];
};

export const groupTournamentsBySeason = (
  tournaments: Tournament[],
): Tournament[] => {
  if (!Array.isArray(tournaments)) return [];

  return tournaments.map((tournament) => {
    const start = dayjs(tournament.startDate);
    const end = dayjs(tournament.endDate);

    const matchedSeason = SEASON_DEFINITIONS.find(
      ({ start: s, end: e }) =>
        start.isAfter(dayjs(s)) && end.isBefore(dayjs(e)),
    );

    return {
      ...tournament,
      season: matchedSeason?.id ?? 0,
    };
  });
};

export const isElevatedRound = (
  tournamentSeason: number,
  tournamentName: string,
) =>
  tournamentSeason === 2 &&
  (tournamentName?.toLowerCase().includes("round 3") ||
    tournamentName?.toLowerCase().includes("round 6"));

export const isRoundNumber456 = (tournamentName: string) =>
  tournamentName?.toLowerCase().includes("round 4") ||
  tournamentName?.toLowerCase().includes("round 5") ||
  tournamentName?.toLowerCase().includes("round 6");

export const getSeason4TournamentSubtitle = (
  tournamentStartDate: string,
  tournamentEndDate: string,
) => {
  const parseDate = (dateString: any) => {
    if (!dateString) return null;

    // Handle ISO strings like "2025-09-20T21:59:00.000Z"
    if (dateString.includes("T")) return dayjs(dateString);

    // Handle "2025-10-14 00:00" format (non-ISO)
    return dayjs(dateString, "YYYY-MM-DD HH:mm");
  };

  const tournamentStart = parseDate(tournamentStartDate);
  const tournamentEnd = parseDate(tournamentEndDate);

  const nextSeriesStart = dayjs("2025-12-10");
  const nextSeriesEnd = dayjs("2026-02-13");

  if (
    tournamentStart?.isAfter(nextSeriesStart) &&
    tournamentEnd?.isBefore(nextSeriesEnd)
  ) {
    return "NEXT World Series";
  }

  return "Qualifier";
};

export const getSeason5TournamentSubtitle = (tournamentName: string) => {
  const name = tournamentName?.toLowerCase() ?? "";

  if (name.includes("next world series")) return "NEXT World Series";
  if (name.includes("next tier") || name.includes("challenger"))
    return "Challenger";

  if (name.includes("tour championship")) return "Finale";

  return "Qualifier";
};

export const getTournamentType = (season: number, id: string, name: string) => {
  const lower = name?.toLowerCase() ?? "";

  return {
    // Common identifiers
    isFinalSeason1: id === TOURNAMENT_ID_SEASON_1_FINAL,
    isFinalSeason2: season === 2 && lower.includes("final"),
    isFinalSeason3: season === 3 && lower.includes("final"),

    // PGA-specific
    isPGASeason3: season === 3 && id === PGA_TOURNAMENT_ID_SEASON_3,
    isPGAAmericaS4: season === 4 && lower.includes("pga of america"), //Trackman Virtual Open - PGA of America only
    isPGAAustraliaS4: season === 4 && lower.includes("pga of australia"), //PGA of Australia Professional Open
    isPGACanadaS4: season === 4 && lower.includes("pga of canada"), //PGA of Canada Professional Open
    isOpenQualifierS4: season === 4 && lower.includes("open qualifier"), //Open Qualifier 1, 2
    isEpsonS4: season === 4 && lower.includes("epson"),
    isNextWorldSeries:
      season === 4 && lower.includes("next world series stage"),
    isNextTourChampionship: season === 4 && lower.includes("tour championship"),

    // Season 5
    isChallengersS5:
      season === 5 && (lower.includes("tier") || lower.includes("challenger")),
    isPGAAmericaS5: season === 5 && lower.includes("pga of america"),
    isPGAAustraliaS5: season === 5 && lower.includes("pga of australia"),
    isPGACanadaS5: season === 5 && lower.includes("pga of canada"),
    isOpenQualifierS5: season === 5 && lower.includes("open qualifier"),
    isEpsonS5: season === 5 && lower.includes("epson"),
    isNextWorldSeriesS5: season === 5 && lower.includes("next world series"),
    isNextTourChampionshipS5:
      season === 5 && lower.includes("tour championship"),

    // Elevated rounds
    isElevated:
      season === 2 && (lower.includes("round 3") || lower.includes("round 6")),
  };
};

/** Currency prefix shown before amounts: season 5 PGA of Australia is "AUD ", PGA of Canada "CAD ", everything else "$". */
export const getCurrency = (
  tournamentSeason: number,
  tournamentName: string,
) => {
  const type = getTournamentType(tournamentSeason, "", tournamentName);
  if (type.isPGAAustraliaS5) return "AUD ";
  if (type.isPGACanadaS5) return "CAD ";
  return "$";
};

export const getPurse = (
  tournamentSeason: number,
  tournamentId: string,
  tournamentName: string,
  playersCount: number,
) => {
  const type = getTournamentType(
    tournamentSeason,
    tournamentId,
    tournamentName,
  );

  // === Season 5 ===
  if (tournamentSeason === 5) {
    if (type.isChallengersS5)
      return (
        INITIAL_PURSE_CHALLENGERS_S5 + ENTRY_FEE_CHALLENGERS_S5 * playersCount
      );
    if (type.isEpsonS5)
      return (
        INITIAL_PURSE_EPSON_S5 +
        SPONSOR_PURSE_EPSON_S5 +
        ENTRY_FEE_EPSON_S5 * playersCount
      );
    if (type.isNextWorldSeriesS5) return INITIAL_PURSE_NEXT_WORLD_SERIES_S5;
    if (type.isNextTourChampionshipS5)
      return INITIAL_PURSE_NEXT_CHAMPIONSHIP_S5;
    if (type.isPGAAustraliaS5)
      return (
        INITIAL_PURSE_PGA_AUSTRALIA_S5 +
        SPONSOR_PURSE_PGA_AUSTRALIA_S5 +
        ENTRY_FEE_PGA_AUSTRALIA_S5 * playersCount
      );
    if (type.isPGACanadaS5)
      return (
        INITIAL_PURSE_PGA_CANADA_S5 +
        SPONSOR_PURSE_PGA_CANADA_S5 +
        ENTRY_FEE_PGA_CANADA_S5 * playersCount
      );
    if (type.isPGAAmericaS5)
      return INITIAL_PURSE_PGA_S5 + ENTRY_FEE_PGA_S5 * playersCount;

    // Qualifiers and anything else
    return INITIAL_PURSE_S5 + ENTRY_FEE_S5 * playersCount;
  }

  // === Season 4 ===
  /** for the NEXT World Series purse is constant */
  if (type.isEpsonS4)
    return INITIAL_PURSE_EPSON + ENTRY_FEE_EPSON * playersCount;
  if (type.isNextWorldSeries) return INITIAL_PURSE_NEXT_WORLD_SERIES;
  if (type.isNextTourChampionship) return INITIAL_PURSE_NEXT_CHAMPIONSHIP;

  // === PGA tournaments ===
  if (type.isPGAAustraliaS4) {
    return (
      INITIAL_PURSE_PGA_AUSTRALIA +
      ENTRY_FEE_PGA_AUSTRALIA_SEASON_4 * playersCount
    );
  }

  if (type.isPGACanadaS4) {
    //initial purse is the same as australia tournament
    return (
      INITIAL_PURSE_PGA_AUSTRALIA + ENTRY_FEE_PGA_CANADA_SEASON_4 * playersCount
    );
  }

  if (type.isPGASeason3 || type.isPGAAmericaS4)
    return INITIAL_PURSE_PGA + ENTRY_FEE_PGA * playersCount;

  // === Finals ===
  if (type.isFinalSeason3) return INITIAL_PURSE_SEASON_3_FINAL;
  if (type.isFinalSeason2 || type.isFinalSeason1) return INITIAL_PURSE;

  // === Default ===
  const base = type.isElevated ? INITIAL_PURSE_ELEVATED : INITIAL_PURSE;
  return base + ENTRY_FEE * playersCount;
};

export const getEntryFee = (
  tournamentSeason: number,
  tournamentId: string,
  tournamentName: string,
) => {
  const type = getTournamentType(
    tournamentSeason,
    tournamentId,
    tournamentName,
  );

  // === Season 5 ===
  if (tournamentSeason === 5) {
    if (type.isChallengersS5) return ENTRY_FEE_CHALLENGERS_S5;
    if (type.isEpsonS5) return ENTRY_FEE_EPSON_S5;
    if (type.isPGAAustraliaS5) return ENTRY_FEE_PGA_AUSTRALIA_S5;
    if (type.isPGACanadaS5) return ENTRY_FEE_PGA_CANADA_S5;
    if (type.isNextWorldSeriesS5 || type.isNextTourChampionshipS5)
      return ENTRY_FEE_NEXT_SERIES_S5;
    if (type.isPGAAmericaS5) return ENTRY_FEE_PGA_S5;

    return ENTRY_FEE_WITH_TAX_S5;
  }

  // === Season 4: special cases ===
  if (type.isEpsonS4) return ENTRY_FEE_EPSON;
  if (type.isPGACanadaS4) return ENTRY_FEE_PGA_CANADA_SEASON_4;
  if (type.isPGAAustraliaS4) return ENTRY_FEE_PGA_AUSTRALIA_SEASON_4;
  if (type.isNextWorldSeries || type.isNextTourChampionship)
    return ENTRY_FEE_NEXT_SERIES;

  // === PGA tournaments ===
  if (type.isPGASeason3 || type.isPGAAmericaS4) return ENTRY_FEE_PGA;

  // === Finals ===
  if (type.isFinalSeason1 || type.isFinalSeason2 || type.isFinalSeason3)
    return 0;

  // === Default ===
  return ENTRY_FEE_WITH_TAX;
};

export const getPrizeTable = (
  tournamentSeason: number,
  tournamentId: string,
  tournamentName: string,
  playersCount: number,
) => {
  const type = getTournamentType(
    tournamentSeason,
    tournamentId,
    tournamentName,
  );
  const isRound456 = isRoundNumber456(tournamentName);

  // === Season 5 ===
  if (tournamentSeason === 5) {
    if (type.isChallengersS5) return prizeCalculatorChallengersS5(playersCount);
    if (type.isPGAAmericaS5) return prizeCalculatorPGAAmericaS5(playersCount);
    if (type.isPGAAustraliaS5) return prizeCalculatorPGA_AU_S5(playersCount);
    if (type.isPGACanadaS5) return prizeCalculatorPGA_CA_S5(playersCount);
    if (type.isNextTourChampionshipS5)
      return prizeCalculatorChampionshipS5(playersCount);
    if (type.isEpsonS5) return prizeCalculatorEpsonS5(playersCount);
    if (type.isOpenQualifierS5)
      return prizeCalculatorOpenQualifierS5(playersCount);
    if (type.isNextWorldSeriesS5)
      return prizeCalculatorWorldSeriesS5(playersCount);
    return prizeCalculatorSeason3(playersCount);
  }

  // === Season 4 ===
  if (tournamentSeason === 4) {
    if (type.isEpsonS4) {
      return prizeCalculatorEpson(playersCount);
    }
    if (type.isNextTourChampionship)
      return prizeCalculatorChampionship(playersCount);

    if (type.isPGAAmericaS4) return prizeCalculatorPGA(playersCount, 4);
    if (type.isPGAAustraliaS4 || type.isPGACanadaS4) {
      return prizeCalculatorPGA_AU_CA(
        playersCount,
        type.isPGAAustraliaS4 ? "isPGAAustraliaS4" : "isPGACanadaS4",
      );
    }
    if (type.isNextWorldSeries) {
      return prizeCalculatorWorldSeries(playersCount);
    }
    return prizeCalculatorSeason3(playersCount);
  }

  // === Season 3 ===
  if (tournamentSeason === 3) {
    if (type.isPGASeason3) return prizeCalculatorPGA(playersCount, 3);
    if (type.isFinalSeason3) return PRIZES_FINAL_SEASON_3;
    return prizeCalculatorSeason3(playersCount);
  }

  // === Season 2 Finals ===
  if (type.isFinalSeason2) {
    return [...PRIZES_FINAL_SEASON_2, ...Array.from(Array(90)).map(() => 0)];
  }

  // === Default (Season 2 non-final) ===
  return prizeCalculatorSeason2(playersCount, type.isElevated, isRound456);
};
